import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { invalidatePatientData, medicationsQuery } from "@/portals/patient/api/patientQueries";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import type { Medication } from "@/shared/types";

interface MedicationsResult {
  medications: Medication[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY: Medication[] = [];

/**
 * The viewed profile's medications across ALL uploads and syncs, fetched on
 * their own (resource_types=MedicationRequest) rather than sliced out of the
 * several-MB every-type records fetch — the Medications tab and the
 * dashboard's medication card never wait on that one. Anything that
 * refreshes records (upload finished, review answered or decided, profile
 * switched) refreshes this too: both live under the profile's query scope.
 */
export function useMedications(): MedicationsResult {
  const { activeDependent } = useFamily();
  const profileId = activeDependent?.id;
  const client = useQueryClient();
  const query = useQuery(medicationsQuery(profileId));
  const medications = useMemo(() => query.data?.map(adaptMedication) ?? EMPTY, [query.data]);
  const refresh = useCallback(() => void invalidatePatientData(client, profileId), [client, profileId]);
  return {
    medications,
    isLoading: query.isPending,
    error: query.isError && query.data === undefined ? "Couldn't load your medications. Please try again later." : null,
    refresh,
  };
}
